import React from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { 
  ChartBarIcon,
  TrophyIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';

interface QuickActionsProps {
  onAddMetric: () => void;
  onAddAchievement: () => void;
  onRecordFeedback: () => void;
  onCreateCard: () => void;
  onPrepareReview?: () => void;
  focus?: any; // To access review end date
}

const QuickActions: React.FC<QuickActionsProps> = ({
  onAddMetric,
  onAddAchievement,
  onRecordFeedback,
  onCreateCard,
  onPrepareReview,
  focus
}) => {
  const { darkMode } = useDarkMode();
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const buttonBg = darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100';
  const buttonText = darkMode ? 'text-gray-200' : 'text-gray-700';
  const primaryButtonBg = 'bg-purple-600 hover:bg-purple-700';
  
  // Calculate if we're within 3 weeks of review end date
  const isNearReviewTime = () => {
    if (!focus?.end_date) return false;
    
    const endDate = new Date(focus.end_date);
    const today = new Date();
    const threeWeeksFromNow = new Date(today.getTime() + (21 * 24 * 60 * 60 * 1000)); // 21 days
    
    return endDate <= threeWeeksFromNow;
  };
  
  const shouldHighlightPrepareReview = isNearReviewTime();
  
  const actions = [
    {
      icon: <ChartBarIcon className="w-5 h-5" />,
      label: 'Add metric',
      onClick: onAddMetric
    },
    {
      icon: <TrophyIcon className="w-5 h-5" />,
      label: 'Add achievement',
      onClick: onAddAchievement
    },
    {
      icon: <ChatBubbleLeftRightIcon className="w-5 h-5" />,
      label: 'Record feedback',
      onClick: onRecordFeedback
    },
    {
      icon: <DocumentTextIcon className="w-5 h-5" />,
      label: 'Create career card',
      onClick: onCreateCard
    }
  ];
  
  // Add Prepare for Review as a regular action when not highlighted
  if (onPrepareReview && !shouldHighlightPrepareReview) {
    actions.push({
      icon: <ClipboardDocumentCheckIcon className="w-5 h-5" />,
      label: 'Prepare for Review',
      onClick: onPrepareReview
    });
  }
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg shadow-sm p-6`}>
      <h3 className={`text-lg font-semibold ${textColor} mb-4`}>Quick Actions</h3>
      
      <div className="space-y-2">
        {onPrepareReview && shouldHighlightPrepareReview && (
          <button
            onClick={onPrepareReview}
            className={`w-full text-left px-4 py-3 ${primaryButtonBg} rounded-lg text-sm text-white transition-colors flex items-center gap-3 mb-2`}
          >
            <ClipboardDocumentCheckIcon className="w-5 h-5" />
            Prepare for Review
          </button>
        )}
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={action.onClick}
            className={`w-full text-left px-4 py-3 ${buttonBg} rounded-lg text-sm ${buttonText} transition-colors flex items-center gap-3`}
          >
            {action.icon}
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default QuickActions;